import twclsx from 'utils/twclsx';

import styles from './Tag.module.css';

const baseClasses = 'inline-flex items-center text-body-mobile-sm px-5 rounded-md text-black';

// Static class maps - all classes must be literal strings
const bgColorClassMap = {
    default: styles['tag-bg-color-mango'],
    primary: styles['tag-bg-color-primary'],
    info: styles['tag-bg-color-info'],
    success: styles['tag-bg-color-success'],
    blueberry: styles['tag-bg-color-blueberry'],
    error: styles['tag-bg-color-error'],
    warning: styles['tag-bg-color-warning'],
    kale: styles['tag-bg-color-kale'],
    lime: styles['tag-bg-color-lime'],
    malt: styles['tag-bg-color-malt'],
    offWhite: styles['tag-bg-color-off-white'],
    special: styles['tag-bg-color-special'],
    grey: styles['tag-bg-color-grey-light'],
    bestSeller: styles['tag-bg-color-best-seller'],
    new: styles['tag-bg-color-new'],
    lastChance: styles['tag-bg-color-last-chance'],
    white: styles['tag-bg-color-white'],
    shopNow: styles['tag-bg-color-shop-now'],
    cranberry: styles['tag-bg-color-cranberry'],
    plusMembership: null, // Special case - handled separately
};

const variantClassMap = {
    xLight: styles['tag-variant-xlight'],
    light: styles['tag-variant-light'],
    medium: styles['tag-variant-medium'],
    dark: styles['tag-variant-dark'],
};

const fontSizes = {
    default: 'text-body-mobile-sm',
    mobileXS: 'text-body-mobile-xs',
    mobileXXS: 'text-body-mobile-xxs leading-snug',
};

const DARK_BACKGROUNDS_REQUIRING_WHITE_TEXT = ['blueberry', 'cranberry', 'error', 'special', 'success'];

/**
 * @param {object} props
 * @param {keyof typeof bgColorClassMap} [props.bgColor='default'] - Background color variant
 * @param {boolean} [props.bold=true] - Whether to use bold font
 * @param {React.ReactNode} props.children - Content to display in the tag
 * @param {keyof typeof fontSizes} [props.fontSize='default'] - Font size variant
 * @param {boolean} [props.roundedFull=false] - Whether to use fully rounded corners
 * @param {keyof typeof variantClassMap} [props.variant='light'] - Color opacity variant
 */
function Tag({
    bgColor = 'default',
    bold = true,
    children,
    fontSize = 'default',
    roundedFull = false,
    variant = 'light',
}) {
    // Handle gradient case - gradients use Tailwind utility classes and don't support
    // the variant opacity system (which relies on color-mix with CSS custom properties)
    if (bgColor === 'plusMembership') {
        return (
            <span
                className={twclsx(baseClasses, 'bg-gradient-berry-blend', fontSizes[fontSize], {
                    'text-white': true,
                    'font-grotesk-bold': bold,
                    'rounded-full': roundedFull,
                })}
            >
                {children}
            </span>
        );
    }

    return (
        <span
            className={twclsx(
                baseClasses,
                bgColorClassMap[bgColor] ?? bgColorClassMap.default,
                variantClassMap[variant] ?? variantClassMap.light,
                fontSizes[fontSize],
                {
                    'text-white': DARK_BACKGROUNDS_REQUIRING_WHITE_TEXT.includes(bgColor) && variant === 'dark',
                    'text-grey': bgColor === 'grey',
                    'text-black if:text-white': bgColor === 'primary',
                    'font-grotesk-bold': bold,
                    'rounded-full': roundedFull,
                }
            )}
        >
            {children}
        </span>
    );
}

export default Tag;
